import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { Box, LinearProgress } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDrawerTriggerNotification, useRecommendationsDrawerContext } from "../../../../contexts/RecommendationsDrawerContext";
import { useRequestsContext } from "../../../../contexts/RequestsContext";
import { useSidePanelHost } from "../../../../contexts/sidePanel/SidePanelHostContext";
import type { SidePanelHost } from "../../../../contexts/sidePanel/sidePanelTypes";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { useFormatPersonName } from "../../../../hooks/useNameFormat";
import { useRosterLookup } from "../../../../hooks/useRosterLookup";
import { NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { formatMonthRangeWithYear } from "../../../../utils/dateFormatting";
import { isNurseActiveInDepartment } from "../../../../utils/formatters";
import { type Nurse, type NurseRole, RecommendationOpenSource } from "../../../../utils/models";
import { toIsoDate } from "../../../../utils/scheduleWeeks";
import { countRequestsWithResponses, rolesMap } from "../../../../utils/utilFunctions";
import { DrawerTriggerButton } from "../../../DrawerTriggerButton/DrawerTriggerButton";
import { StaffAutoComplete } from "../../../UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { EmptyState } from "../../../UIToolkit/molecules/EmptyState";
import { SystemBanner } from "../../../UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { ScheduleExportButton } from "../../ScheduleExport/ScheduleExportButton";
import { StaffingDateTypeSelector } from "../../StaffingDateTypeSelector";
import { StaffingViewLoading } from "../../StaffingViewLoading";
import { MonthlyDaySidebar } from "./MonthlyDaySidebar";
import { MonthlyFilterBar } from "./MonthlyFilterBar";
import { MonthlyJobGrid } from "./MonthlyJobGrid";
import { MonthlyViewConfigButton } from "./MonthlyViewConfigButton";
import type { MonthlyDayFilters } from "./monthlyJobUtils";
import { useMonthlyJobViewState } from "./useMonthlyJobViewState";

/**
 * This view is a flex ROW item of `ContentWrapper.module.scss` `.window` (`flex-direction: row;
 * align-items: flex-start`), so without these it sizes to its content and renders as a narrow strip
 * roughly two thirds of the page wide. Mirrors the sibling `.staffContainer` in `Staffing.module.scss`;
 * `alignSelf: stretch` is what defeats the parent's `align-items: flex-start`. `.window` is shared by
 * every page, so the fix belongs here and not there.
 */
const FILL_PARENT_SX = { flex: 1, alignSelf: "stretch", width: "100%", minWidth: 0, height: "100%" } as const;

export const MonthlyJobView: FC = () => {
	const { t } = useTranslation();
	const state = useMonthlyJobViewState();
	const { chosenDepartment } = useChosenDepartment();
	const { isAutoScheduler } = useDepartmentFlags();
	const { roster, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);

	const { isOpen: drawerOpen, toggleDrawerOpen } = useRecommendationsDrawerContext();
	const { getDrawerTriggerNotification } = useDrawerTriggerNotification();
	const { staffRequests } = useRequestsContext();
	const requestsWithResponsesCount = useMemo(() => countRequestsWithResponses(staffRequests), [staffRequests]);
	const notification = getDrawerTriggerNotification(requestsWithResponsesCount);

	const formatPersonName = useFormatPersonName();
	const nurseOptions = useMemo<SystemAutoCompleteOption<Nurse>[]>(
		() =>
			// DEFAULT: no one to search for until the roster loads.
			(roster ?? [])
				.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
				.map((nurse) => ({ label: formatPersonName(nurse), value: nurse })),
		[roster, chosenDepartment.department_id, formatPersonName],
	);

	const availableLocations = useMemo(
		() => [...new Set(state.locations.map((location) => location.location).filter((name): name is string => Boolean(name)))],
		[state.locations],
	);

	// Window / role / job / location filters narrow the aggregated totals. Staff search is deliberately NOT
	// here — it only highlights the searched nurse's days (see `highlightNurseId`), so other staff are never
	// undercounted. The job filter is reconciled against the currently-visible jobs so a selection that's
	// since been hidden via the settings config can't zero out the whole grid (an empty result = "show all").
	const filters = useMemo<MonthlyDayFilters>(() => {
		const visibleIds = new Set(state.visibleJobs.map((job) => job.id));
		const jobFilterIds = new Set(state.jobFilterIds.filter((id) => visibleIds.has(id)));
		// Shifts carry the FULL role name, the selector emits the short one.
		const roleFilter = new Set(state.roleFilter.filter((role): role is NurseRole => role !== "all").map((role) => rolesMap[role] as string));
		// Empty OR fully populated both mean "show all" — `DayViewLocationFilter`'s own semantics.
		const locationFilter = new Set(state.locationFilter.length === availableLocations.length ? [] : state.locationFilter);
		return { jobFilterIds, roleFilter, locationFilter, windowKey: state.windowKey };
	}, [state.jobFilterIds, state.roleFilter, state.locationFilter, state.windowKey, state.visibleJobs, availableLocations.length]);

	// --- Sidebar ---
	// Stable identity so the memoized `MonthlyJobCell` isn't re-rendered by every router-driven URL write.
	const openSidebar = state.openSidebar;
	const handleOpenSidebar = useCallback((date: DateTime, focusJobId?: string) => openSidebar({ date, focusJobId }), [openSidebar]);

	// Stable identities so the memoized `MonthlyJobCell` isn't re-rendered by every router-driven URL write.
	const openDayView = state.handleOpenDayView;
	const handleOpenDayViewFromCell = useCallback((date: DateTime) => openDayView(date, "cell"), [openDayView]);
	const handleOpenDayViewFromSidebar = useCallback((date: DateTime) => openDayView(date, "sidebar"), [openDayView]);

	const handleDrawerClose = useCallback(() => {
		toggleDrawerOpen(false, RecommendationOpenSource.WeeklyView);
	}, [toggleDrawerOpen]);
	// The admin side panel docks over the month's right edge; the month has no recommendations, so no such tab.
	const sidePanelHost = useMemo<SidePanelHost>(() => ({ recommendations: null, onClose: handleDrawerClose }), [handleDrawerClose]);
	useSidePanelHost(sidePanelHost);

	const selectedDateKey = state.sidebarSelection && toIsoDate(state.sidebarSelection.date);

	const loadFailedBanner = (title: string) => (
		<SystemBanner
			tone="error"
			title={title}
			action={
				<SystemButton buttonVariant="secondary" size="small" onClick={state.handleRetry} data-testid="monthly-job-view-retry">
					{t("retry")}
				</SystemButton>
			}
			sx={{ mx: 2, mt: 1 }}
			dataTestId="monthly-job-view-error"
		/>
	);

	// Undefined until the first week arrives from the schedule cache; with the flag off the provider has already
	// loaded, so it is known at once. When every week failed there is nothing to show but the error and Retry.
	if (state.hasSchedule === undefined) {
		if (state.isLoading) return <StaffingViewLoading dataTestId="monthly-job-view-loading" />;
		return (
			<Box
				data-testid="monthly-job-view-root"
				sx={{ display: "flex", alignItems: "center", justifyContent: "center", ...FILL_PARENT_SX, backgroundColor: SURFACE }}
			>
				{loadFailedBanner(t("monthly.view.load.failed"))}
			</Box>
		);
	}
	if (!state.hasSchedule) {
		return (
			<Box
				data-testid="monthly-job-view-root"
				sx={{ display: "flex", alignItems: "center", justifyContent: "center", ...FILL_PARENT_SX, backgroundColor: SURFACE }}
			>
				<EmptyState
					icon={<CalendarMonthOutlinedIcon />}
					title={t("monthly.view.empty.title")}
					hint={t("monthly.view.empty.hint")}
					dataTestId="monthly-job-view-empty"
				/>
			</Box>
		);
	}

	return (
		<Box data-testid="monthly-job-view-root" sx={{ display: "flex", flexDirection: "column", ...FILL_PARENT_SX, backgroundColor: SURFACE, overflow: "hidden" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 1.5,
					px: 2,
					py: 1,
					borderBottom: `1px solid ${NEUTRAL[300]}`,
				}}
			>
				<StaffingDateTypeSelector
					currentDateText={formatMonthRangeWithYear(state.currentTimeFrameStartDate, state.currentTimeFrameEndDate)}
					onResetButtonClick={state.handleToday}
					onNextClick={state.handleNext}
					onPreviousClick={state.handlePrev}
					initialPageType="MONTHLY"
					isPrevDisabled={state.isPrevDisabled}
					isNextDisabled={state.isNextDisabled}
					currentDate={state.currentTimeFrameStartDate}
					visibleRangeEnd={state.currentTimeFrameEndDate}
					onDateSelected={state.handleDatePickerSelected}
					minDate={state.minDate}
					maxDate={state.maxDate}
				/>
				<StaffAutoComplete
					nurses={nurseOptions}
					value={state.searchedNurse}
					onChange={(_, value) => state.setSearchedNurse(value)}
					disabled={!roster}
					helperText={isRosterError ? t("profile.roster.unavailable") : undefined}
					width="220px"
				/>
				<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
					<ScheduleExportButton weekIndex={state.weekIndex} />
					<MonthlyViewConfigButton
						totalsMode={state.totalsMode}
						onTotalsModeChange={state.setTotalsMode}
						enabledJobs={state.enabledJobs}
						visibleJobIds={new Set(state.visibleJobs.map((job) => job.id))}
						onVisibleJobsChange={state.setVisibleJobIds}
					/>
					<DrawerTriggerButton open={drawerOpen} notification={notification} onClick={() => toggleDrawerOpen(true, RecommendationOpenSource.WeeklyView)} />
				</Box>
			</Box>

			<MonthlyFilterBar
				windows={state.shiftWindows}
				windowKey={state.windowKey}
				onWindowKeyChange={state.setWindowKey}
				roleFilter={state.roleFilter}
				onRoleFilterChange={state.setRoleFilter}
				jobFilterIds={state.jobFilterIds}
				onJobFilterChange={state.setJobFilterIds}
				visibleJobs={state.visibleJobs}
				selectedLocations={state.locationFilter}
				setSelectedLocations={state.setLocationFilter}
				availableLocations={availableLocations}
				totalsMode={state.totalsMode}
			/>

			{state.isError && loadFailedBanner(t("monthly.view.weeks.load.failed"))}
			{/* Weeks arrive one by one; until they all have, the days still on their way show empty. */}
			{state.isLoading && <LinearProgress data-testid="monthly-job-view-weeks-loading" />}

			{/* Sizes the grid, but is NOT a scroll owner: `MonthlyJobGrid` owns the only vertical scroll box,
			    so the two nested `overflowY: auto` that used to fight each other are down to one. */}
			<Box sx={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", px: 2, py: 1 }}>
				<MonthlyJobGrid
					visibleWeeks={state.visibleWeeks}
					visibleJobs={state.countedJobs}
					windows={state.shiftWindows}
					totalsMode={state.totalsMode}
					filters={filters}
					today={state.today}
					isPastDay={state.isPastDay}
					isAutoScheduler={isAutoScheduler}
					highlightNurseId={state.searchedNurse?.value.id}
					onOpenSidebar={handleOpenSidebar}
					onOpenDayView={handleOpenDayViewFromCell}
					onWeekNumberClick={state.handleWeekNumberClick}
				/>
			</Box>

			{state.sidebarSelection && (
				// Remount-keyed on the day AND the focused job. The drawer seeds its own job-chip selection
				// from `initialFocusJobId` in `useState`, and it stays mounted while `openSidebar` swaps the
				// selection underneath it — so without this, clicking an Aide pill on one day and then any
				// other day re-opened the drawer still filtered to Aides, with no chip explaining why.
				<MonthlyDaySidebar
					key={`${state.sidebarSelection.date.toFormat("yyyy-MM-dd")}:${state.sidebarSelection.focusJobId ?? "all"}`}
					day={selectedDateKey ? state.daysByDate.get(selectedDateKey) : undefined}
					isInSchedule={state.isDateInSchedule(state.sidebarSelection.date)}
					date={state.sidebarSelection.date}
					showActual={state.isPastDay(state.sidebarSelection.date)}
					visibleJobs={state.countedJobs}
					windows={state.shiftWindows}
					dayBoundary={state.dayBoundary}
					departmentId={state.departmentId}
					filters={filters}
					isAutoScheduler={isAutoScheduler}
					initialFocusJobId={state.sidebarSelection.focusJobId}
					onOpenDayView={handleOpenDayViewFromSidebar}
					onClose={state.closeSidebar}
				/>
			)}
		</Box>
	);
};
